/**
 * Per-photo quantity editor (round 7). Cart/Order lines that span MULTIPLE
 * photos used to step quantities blindly (+ silently bumped whichever photo
 * happened to be last) — now the line's qty control opens THIS compact dialog:
 * one row per photo (thumbnail + filename + stepper), a running group total,
 * and an "Even out" button that sets every photo back to the same quantity.
 *
 * Drafts commit on "Update quantities" (Esc/Cancel discards): patches come
 * from cartModel.perPhotoQtyPlan — growth lands on a photo's last member
 * print, shrink walks back, 0 removes that photo's prints from the line. The
 * group itself must keep ≥ 1 print (an all-zero draft can't commit — remove
 * the line with ✕ instead). Used by BOTH the Step-2 Order pane and the Step-3
 * cart GroupCard; single-photo lines keep their direct stepper.
 */

import { useMemo, useState } from 'react';
import type { UiPrint } from '../../lib/types';
import { useStore } from '../../store/store';
import { ModalShell } from '../sizepaper/ModalShell';
import { QtyStepper } from '../sizepaper/QtyStepper';
import { evenQty, perPhotoQtyPlan, photoQtyRows } from './cartModel';

export interface PhotoQtyDialogProps {
  /** The line's member prints (one cart group / one order-pane row). */
  members: readonly UiPrint[];
  photosById: ReadonlyMap<string, { previewUrl: string | null; filename: string }>;
  /** Line description shown under the title, e.g. `4 × 6" C-Type · Gloss`. */
  title: string;
  onClose: () => void;
}

/**
 * A qty-stepper-shaped BUTTON for ambiguous lines: any tap (−, the number,
 * or +) opens a quantity editor instead of blindly stepping the group.
 * Defaults describe the per-photo editor; the per-print editor (round 9)
 * passes its own copy.
 */
export function QtyOpenButton({
  qty,
  label,
  className,
  onOpen,
  title = 'Choose which photo gets more prints',
  ariaPrefix = 'Change quantities per photo',
}: {
  qty: number;
  label: string;
  className?: string;
  onOpen: () => void;
  title?: string;
  ariaPrefix?: string;
}) {
  return (
    <button
      type="button"
      className={`qty qty--open${className ? ` ${className}` : ''}`}
      title={title}
      aria-label={`${ariaPrefix}, ${label}`}
      onClick={onOpen}
    >
      <span className="qty__btn" aria-hidden>
        −
      </span>
      <span className="qty__num" aria-hidden>
        {qty}
      </span>
      <span className="qty__btn" aria-hidden>
        +
      </span>
    </button>
  );
}

export function PhotoQtyDialog({ members, photosById, title, onClose }: PhotoQtyDialogProps) {
  const updatePrint = useStore((s) => s.updatePrint);
  const removePrint = useStore((s) => s.removePrint);

  // Row skeleton is frozen at open (photo order + starting quantities); the
  // drafts layer on top so rows never jump or vanish mid-edit.
  const rows = useMemo(() => photoQtyRows(members), [members]);
  const [drafts, setDrafts] = useState<ReadonlyMap<string, number>>(
    () => new Map(rows.map((r) => [r.photoId, r.qty])),
  );

  const draftOf = (photoId: string, fallback: number) => drafts.get(photoId) ?? fallback;
  const total = rows.reduce((a, r) => a + draftOf(r.photoId, r.qty), 0);
  const even = evenQty(rows.map((r) => ({ qty: draftOf(r.photoId, r.qty) })));
  const allEven = rows.every((r) => draftOf(r.photoId, r.qty) === even);
  const dirty = rows.some((r) => draftOf(r.photoId, r.qty) !== r.qty);

  const setDraft = (photoId: string, qty: number) => {
    setDrafts((prev) => {
      const next = new Map(prev);
      next.set(photoId, qty);
      return next;
    });
  };

  const evenOut = () => {
    setDrafts(new Map(rows.map((r) => [r.photoId, even])));
  };

  const commit = () => {
    if (total < 1) return;
    for (const patch of perPhotoQtyPlan(members, drafts)) {
      if (patch.qty <= 0) removePrint(patch.printUiId);
      else updatePrint(patch.printUiId, { qty: patch.qty });
    }
    onClose();
  };

  return (
    <ModalShell onClose={onClose} width={460} label="Prints per photo" className="pqty">
      <div className="pqty__head">
        <div>
          <div className="pqty__title">Prints per photo</div>
          <div className="pqty__sub">{title}</div>
        </div>
        <button type="button" className="pqty__close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="pqty__rows">
        {rows.map((r) => {
          const ph = photosById.get(r.photoId);
          const qty = draftOf(r.photoId, r.qty);
          return (
            <div key={r.photoId} className="pqty__row">
              {ph?.previewUrl ? (
                <img className="pqty__thumb" src={ph.previewUrl} alt="" loading="lazy" />
              ) : (
                <span className="pqty__thumb pqty__thumb--ph" />
              )}
              <span className="pqty__name" title={ph?.filename}>
                {ph?.filename ?? 'photo'}
                {qty === 0 && <em className="pqty__gone">removed from this line</em>}
              </span>
              <QtyStepper
                variant="light"
                ariaLabel={`Quantity for ${ph?.filename ?? 'photo'}`}
                value={qty}
                min={0}
                onChange={(next) => setDraft(r.photoId, next)}
              />
            </div>
          );
        })}
      </div>

      <div className="pqty__foot">
        <div className="pqty__totals">
          <span className="pqty__total" aria-live="polite">
            Total: <strong>{total}</strong> print{total === 1 ? '' : 's'}
          </span>
          <button type="button" className="pill-btn" disabled={allEven} onClick={evenOut}>
            Even out · {even} each
          </button>
        </div>
        {total < 1 && (
          <span className="pqty__min">Keep at least 1 print — or remove the whole line with ✕.</span>
        )}
        <div className="pqty__actions">
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={total < 1 || !dirty}
            onClick={commit}
          >
            Update quantities
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
